import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { AmiraResources } from '@/models';
import { getSkillResourcesMulti } from '@/services/weeklyPlanner/query/getSkillResources';
import useRosterInfo from './useRosterInfo';

/**
 * useSkillResources for bulk fetching of resources for multiple skills.
 * Returns: { getResources, resourceMap, isLoading, error, refetch }
 * 
 * Usage:
 * const { getResources, resourceMap, isLoading, error } = useSkillResources();
 * const teacherResources = getResources(teacherSkillIds, 100);
 * const prerequisiteResources = getResources(prerequisiteSkillIds, 100);
 */

interface UseSkillResourcesProps {
  skillIds: string[];
  limit?: number;
  filter?: { providers?: string[] };
}

interface UseSkillResourcesReturn {
  getResources: (skillIds: string[], limit?: number, filter?: { providers?: string[] }) => Record<string, AmiraResources[]>;
  resourceMap: Record<string, AmiraResources[]>;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export const useSkillResources = () => {
  const { districtId, schoolId, status } = useRosterInfo();
  const [ resourceMap, setResourceMap ] = useState<Record<string, AmiraResources[]>>({});
  const [ isLoading, setIsLoading ] = useState(false);
  const [ error, setError ] = useState<string | null>(null);
  const [ pendingRequests, setPendingRequests ] = useState<Set<string>>(new Set());
  
  // Use refs to store the latest values to avoid dependency issues
  const latestValues = useRef({ districtId, schoolId, status });

  // Update refs on every render
  useEffect(() => {
    latestValues.current = { districtId, schoolId, status };
  });

  // Create a stable status key to prevent unnecessary re-renders
  const statusKey = useMemo(() => status?.type || 'UNKNOWN', [status?.type]);

  const fetchResources = useCallback(async (skillIds: string[], limit: number = 100, filter: { providers?: string[] } = {}) => {
    const { 
      districtId: currentDistrictId,
      schoolId: currentSchoolId,
      status: currentStatus,
    } = latestValues.current;
    
    if (!skillIds || skillIds.length === 0) {
      return {};
    }

    if (!currentDistrictId) {
      setError('Missing district information');
      return {};
    }

    if (!currentSchoolId) {
      setError('Missing school information');
      return {};
    }

    // Check if roster is still loading
    if (currentStatus?.type === 'PENDING') {
      setError('Roster is still loading');
      return {};
    }

    // Create a unique key for this request
    const requestKey = [...skillIds].sort().join(',') + `-${limit}`;
    
    // Check if this request is already pending
    if (pendingRequests.has(requestKey)) {
      return resourceMap;
    }

    // Add to pending requests
    setPendingRequests(prev => new Set(prev).add(requestKey));
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await getSkillResourcesMulti(skillIds, currentDistrictId, currentSchoolId, limit, filter);
      // console.log('----====> (useSkillResources) retrieved resources', JSON.stringify(skillIds), JSON.stringify(result));
      // Merge new results with existing resourceMap
      setResourceMap(prev => ({
        ...prev,
        ...result
      }));
      return result;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch skill resources');
      return {};
    } finally {
      setIsLoading(false);
      // Remove from pending requests
      setPendingRequests(prev => {
        const newSet = new Set(prev);
        newSet.delete(requestKey);
        return newSet;
      });
    }
  }, [statusKey]);

  const getResources = useCallback((
    skillIds: string[],
    limit: number = 100,
    filter: { providers?: string[] } = {}
  ) => {
    if (!skillIds || skillIds.length === 0) {
      return {};
    }

    // Check if we already have the data
    const hasAllData = skillIds.every(skillId => resourceMap[skillId]);
  
    if (hasAllData) {
      // Return filtered results for the requested skillIds
      const filteredResults: Record<string, AmiraResources[]> = {};
      skillIds.forEach(skillId => {
        if (resourceMap[skillId]) {
          filteredResults[skillId] = resourceMap[skillId];
        }
      });
      return filteredResults;
    }

    // If we don't have all the data, trigger a fetch
    fetchResources(skillIds, limit, filter);
    
    // Return what we have so far
    const partialResults: Record<string, AmiraResources[]> = {};
    skillIds.forEach(skillId => {
      if (resourceMap[skillId]) {
        partialResults[skillId] = resourceMap[skillId];
      }
    });
    return partialResults;
  }, [resourceMap]);

  const refetch = useCallback(() => {
    // Refetch all currently stored skillIds
    const allSkillIds = Object.keys(resourceMap);
    if (allSkillIds.length > 0) {
      fetchResources(allSkillIds, 100);
    }
  }, [resourceMap]);

  return {
    getResources,
    resourceMap,
    isLoading,
    error,
    refetch,
  };
}; 